/**
 * "{ws} is out of storage": an upload came back `plan_limit_storage` (core `logic/storageLimit#storagePrompt`, held
 * by BillingViewModel `storageFull`). Owners and admins get "Add storage" (the portal), "Choose a plan" (the picker with
 * one add-on) or "Contact us"; members get the owners and admins to ask. Like Slack's storage-limit modal.
 */
import type { BillingScreenState, BillingViewModel, StoragePrompt } from "@bundle/core";
import { Button, Sheet, Stack, Text } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";
import { useBillingFormat, useManagerNames } from "./format.ts";

const STORE_NAME = { app_store: "App Store", play: "Google Play" } as const;

/** The prompt's body (also used by tests and snapshots). */
export function useStorageFullCopy(): (p: StoragePrompt, workspaceName: string) => { title: string; message: string; usage?: string } {
  const t = useT();
  const f = useBillingFormat();
  const names = useManagerNames();
  return (p, ws) => {
    const message =
      p.action === "addStorage"
        ? t("billing.storageFull.addStorage")
        : p.action === "choosePlan"
          ? t("billing.storageFull.choosePlan")
          : p.action === "contact"
            ? t("billing.storageFull.contact", { ws })
            : p.store !== undefined
              ? t("billing.storageFull.store", { store: STORE_NAME[p.store] })
              : p.manage
                ? t("billing.storageFull.web")
                : t("billing.storageFull.member", { names });
    const usage = p.usedBytes !== undefined && p.poolBytes !== undefined ? t("billing.storageFull.usage", { used: f.gb(p.usedBytes), pool: f.gb(p.poolBytes) }) : undefined;
    return { title: t("billing.storageFull.title", { ws }), message, ...(usage === undefined ? {} : { usage }) };
  };
}

export function StorageFullDialog({
  vm,
  state,
  workspaceName,
  onContact,
  testID,
}: {
  vm: BillingViewModel;
  state: BillingScreenState;
  workspaceName: string;
  onContact: () => void;
  testID: string;
}) {
  const t = useT();
  const copy = useStorageFullCopy();
  const prompt = state.storageFull;
  const close = () => vm.dismissStorageFull();
  const body = prompt === undefined ? undefined : copy(prompt, workspaceName);
  const action = prompt?.action;
  return (
    <Sheet open={prompt !== undefined} onClose={close} title={body?.title ?? ""} detents={["medium"]} closeLabel={t("common.close")} testID={testID}>
      {body === undefined ? null : (
        <Stack gap="4">
          <Text color="text.secondary" testID={`${testID}-message`}>
            {body.message}
          </Text>
          {body.usage === undefined ? null : (
            <Text variant="footnote" color="text.secondary" testID={`${testID}-usage`}>
              {body.usage}
            </Text>
          )}
          <Stack direction="horizontal" gap="2" justify="end">
            <Button label={action === undefined ? t("common.close") : t("billing.storageFull.notNow")} variant="secondary" onPress={close} testID={`${testID}-dismiss`} />
            {action === undefined ? null : (
              <Button
                label={action === "addStorage" ? t("billing.addStorage") : action === "choosePlan" ? t("billing.choosePlan") : t("billing.contactPro")}
                variant="primary"
                onPress={() => {
                  if (action === "contact") {
                    close();
                    onContact();
                  } else vm.addStorage();
                }}
                testID={`${testID}-action`}
              />
            )}
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}
